import { normalizeSearch } from '@app/hr/hr-leave-board';
import { SalesOrderState } from '@app/shared/constants-server';
import type { Day, RentalLifecycleFull, RentalLineView, RentalViewStatus } from '@app/sales/rental-billing/rental-billing.model';
import { deliveredDay, returnedDay } from '@app/sales/rental-billing/rental-planning.geometry';
import { rentalSubtitle, rentalTitle, showInterruptedBadge } from '@app/sales/rental-billing/rental-billing.labels';
import { partnerLabel } from '@app/sales/rental-billing/planning/rental-planning.view';
import { parisDay } from '@app/sales/rental-contract/rental-contract.model';
import type { PayerCategory, PayerOrderWire } from './rental-payer.service';

// Un dossier de location du payeur, tel que le résume GET /sales/rental_payers/:id.
export interface PayerDossier {
  id: number;
  state: string;
  openedOn: Day | null;
  recipientId: number | null; // data.recipient s'il diffère du payeur
  recipientName: string | null;
  fse: boolean;               // data.fse_id : dossier pris en charge par la Sécu
  memos: string[];            // patients des lignes RENTAL, sans doublon
  rentalCount: number;        // lignes RENTAL, repris compris
  returnedCount: number;      // dont reprises signées
  lastReturn: Day | null;
  category: PayerCategory;    // calculée par le serveur : en cours, repris, clos, annulé
  closedOn: Day | null;       // dernière clôture d'une de ses lignes
}

// Une rangée par matériel (ligne RENTAL de la vue du moteur).
export interface PayerRow {
  id: number;
  seq: number;
  patient: string;            // mémo de la ligne, sinon bénéficiaire du dossier, sinon vide
  fromDossier: boolean;       // patient pris sur le dossier faute de mémo
  title: string;              // intitulé du contrat de location, l'article sans contrat
  article: string | null;     // l'article sous l'intitulé du contrat
  delivered: Day | null;
  planned: Day | null;
  returned: Day | null;
  billedUntil: Day | null;
  rank: string;
  status: RentalViewStatus;
  interrupted: boolean;
  anomalies: string[];
}

export interface PayerCounts { inPlace: number; toDeliver: number; returned: number; }

export function dossierOf(o: PayerOrderWire, payer: number): PayerDossier {
  const recipient = Number(o?.recipient) || 0;
  const recipientId = recipient > 0 && recipient !== payer ? recipient : null;
  const memos: string[] = [];
  for (const raw of o?.memos ?? []) {
    const m = String(raw ?? '').trim();
    if (m && !memos.includes(m)) memos.push(m);
  }
  return {
    id: Number(o?.id) || 0,
    state: String(o?.state ?? ''),
    openedOn: parisDay(o?.created),
    recipientId,
    recipientName: recipientId ? partnerLabel({ firstname: o.recipient_firstname, name: o.recipient_name }, recipientId) : null,
    fse: !!o?.fse,
    memos,
    rentalCount: Number(o?.rentals) || 0,
    returnedCount: Number(o?.returned) || 0,
    lastReturn: parisDay(o?.last_return),
    category: (['open', 'returned', 'closed', 'canceled'] as const).find((c) => c === o?.category) ?? 'open',
    closedOn: parisDay(o?.closed_at),
  };
}

// Dossier repris ou clos : tout son matériel est revenu ou facturé jusqu'au bout, sa vue se lit en entier (pas `open`).
export function isFinished(d: PayerDossier): boolean {
  return d.category === 'returned' || d.category === 'closed';
}

export function isCanceled(d: PayerDossier): boolean {
  return d.state === SalesOrderState.CANCELED;
}

export function rankOf(l: RentalLineView): string {
  return l.rank > 0 ? `${l.rank}/${l.duration != null && l.duration > 0 ? l.duration : '∞'}` : '';
}

const byPatient = new Intl.Collator('fr', { sensitivity: 'base', numeric: true });

// Rangées triées par patient puis par ordre de saisie : les matériels d'un même patient se suivent.
export function rowsOf(v: RentalLifecycleFull, beneficiary: string | null): PayerRow[] {
  return v.lines
    .filter((l) => l.typ === 'RENTAL')
    .map((l) => {
      const memo = (l.memo ?? '').trim();
      return {
        id: l.id,
        seq: l.seq,
        patient: memo || beneficiary || '',
        fromDossier: !memo && !!beneficiary,
        title: rentalTitle(l) ?? `article ${l.product}`,
        article: rentalSubtitle(l),
        delivered: deliveredDay(l),
        planned: l.planned_on,
        returned: returnedDay(l),
        billedUntil: l.billed_until,
        rank: rankOf(l),
        status: l.status,
        interrupted: showInterruptedBadge(l, v.as_of),
        anomalies: l.anomalies ?? [],
      };
    })
    .sort((a, b) => byPatient.compare(a.patient, b.patient) || a.seq - b.seq);
}

export function countsOf(rows: PayerRow[]): PayerCounts {
  const c: PayerCounts = { inPlace: 0, toDeliver: 0, returned: 0 };
  for (const r of rows) {
    if (r.returned) c.returned++;
    else if (r.delivered) c.inPlace++;
    else c.toDeliver++;
  }
  return c;
}

// Patients d'un dossier : son bénéficiaire, sinon les mémos de ses lignes, sinon le payeur lui-même (patient qui paie).
export function patientKeysOf(d: PayerDossier): string[] {
  if (d.recipientId) return [`r:${d.recipientId}`];
  const memos = [...new Set(d.memos.map(normalizeSearch).filter((m) => !!m))];
  return memos.length ? memos.map((m) => `m:${m}`) : ['payer'];
}

// Doublons de chapeau : dossiers non annulés, avec du matériel listé, même patient ; la Sécu n'y entre pas (JP 04/11 : unicité du couple payeur, bénéficiaire).
export function sameStarts(dossiers: PayerDossier[]): Map<number, number[]> {
  const byKey = new Map<string, number[]>();
  for (const d of dossiers) {
    if (isCanceled(d) || d.rentalCount === 0) continue;
    for (const key of patientKeysOf(d)) {
      byKey.set(key, [...(byKey.get(key) ?? []), d.id]);
    }
  }
  const out = new Map<number, number[]>();
  for (const ids of byKey.values()) {
    if (ids.length < 2) continue;
    for (const id of ids) out.set(id, [...new Set([...(out.get(id) ?? []), ...ids.filter((x) => x !== id)])].sort((a, b) => a - b));
  }
  return out;
}

// Recherche d'un patient : le dossier entier si son numéro ou son bénéficiaire répond, sinon ses seules rangées qui répondent.
export function filterRows(rows: PayerRow[], q: string, head: string): PayerRow[] {
  const n = normalizeSearch(q);
  if (!n || normalizeSearch(head).includes(n)) return rows;
  return rows.filter((r) => normalizeSearch(`${r.patient} ${r.title} ${r.article ?? ''}`).includes(n));
}

export function headMatches(q: string, head: string, memos: string[]): boolean {
  const n = normalizeSearch(q);
  return !n || normalizeSearch(`${head} ${memos.join(' ')}`).includes(n);
}
